import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  MergeType as MergeTypeIcon
} from '@mui/icons-material';

const AiConsolidateDialog = ({ proposals, onApply, onClose }) => {
  const [applyingKey, setApplyingKey] = useState(null);

  if (!proposals) return null;

  const handleApply = async (proposal) => {
    setApplyingKey(proposal.name);
    await onApply(proposal);
    setApplyingKey(null);
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <AutoAwesomeIcon color="secondary" />
        Consolidate Rules
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {proposals.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            All done. No proposals left.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each proposal replaces several overlapping rules with one broader rule.
              Nothing changes until you click Merge. Review the conditions, as the new
              rule will match more transactions than the old ones did.
            </Typography>
            <Stack divider={<Divider />} spacing={0}>
              {proposals.map((proposal) => (
                <Box key={proposal.name} sx={{ py: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', sm: 'flex-start' },
                      gap: { xs: 1.5, sm: 2 }
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        {proposal.name}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                        {proposal.conditionGroups.map((group, gi) => (
                          <React.Fragment key={gi}>
                            {gi > 0 && (
                              <Chip label="OR" size="small" color="primary" sx={{ fontSize: '0.7rem', height: 18 }} />
                            )}
                            {group.conditions.map((cond, ci) => (
                              <Chip
                                key={ci}
                                label={`${cond.field} ${cond.operator} "${cond.value}"`}
                                size="small"
                                sx={{ fontSize: '0.75rem', height: 22 }}
                              />
                            ))}
                          </React.Fragment>
                        ))}
                        <Typography variant="body2" color="text.secondary">→</Typography>
                        <Chip
                          label={proposal.categoryName}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 22 }}
                        />
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Replaces {proposal.replacedRuleNames.length} rules:{' '}
                        {proposal.replacedRuleNames.join(' · ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                        {proposal.rationale}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      disabled={applyingKey !== null}
                      startIcon={
                        applyingKey === proposal.name
                          ? <CircularProgress size={14} color="inherit" />
                          : <MergeTypeIcon fontSize="small" />
                      }
                      onClick={() => handleApply(proposal)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 600,
                        flexShrink: 0,
                        alignSelf: { xs: 'stretch', sm: 'flex-start' }
                      }}
                    >
                      {applyingKey === proposal.name ? 'Merging...' : `Merge ${proposal.replacedRuleNames.length}`}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {proposals.length === 0 ? 'Close' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiConsolidateDialog;
